import { IonContent, IonSpinner, IonText } from '@ionic/react';
import { orderBy } from 'lodash';
import { ReactElement, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { ChatMessage } from '../../api/graphql/generated';
import { getQueryParameter } from '../../util/router';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { InfiniteScroll } from '../scroll/infinite-scroll';
import './chat.css';
import { useListChatMessageQueryHelper } from './chat.hooks';
import { ConversationPreview } from './conversation-preview';
import { NewMessageChip } from './new-message-chip';

export interface ConversationListProps {
  chatId: string;
}

const ConversationList = (props: ConversationListProps): ReactElement => {
  const { chatId } = props;
  const { i18n } = useTranslation();
  const cursor = getQueryParameter('cursor');
  const contentRef = useRef<HTMLIonContentElement>(null);

  const {
    queryResult: { data: listChatData, loading, error, refetch },
    waitingOnFirstLoad,
    getMoreForInfiniteScroll,
    onScrollEnd,
    onNewMessageClick,
    hasNewMessage,
  } = useListChatMessageQueryHelper({
    chatId,
    contentRef,
    cursor,
    scroll: {
      auto: {
        enabled: true,
        direction: 'top',
      },
    },
    refetchOnViewEnter: true,
    clearCacheOnLoad: true,
  });

  const prevCursor = listChatData?.listChatMessages.prevCursor;

  const [chipRef, { height: chipHeight }] = useMeasure<HTMLDivElement>();
  const items = useMemo(
    () => listChatData?.listChatMessages.items ?? [],
    [listChatData]
  );

  const conversations: ChatMessage[] = useMemo((): ChatMessage[] => {
    return orderBy(items, ['updatedAt'], ['desc']);
  }, [items]);

  return (
    <IonContent
      ref={contentRef}
      scrollEvents={true}
      onIonScrollEnd={onScrollEnd}
    >
      <LoadingRetry
        error={error}
        refetch={refetch}
        loading={loading || waitingOnFirstLoad}
        results={{
          hasResults: conversations.length > 0,
          noResultsComponent: (
            <div className="full-height-width content-center">
              <IonText className="ion-padding ion-text-center" color="medium">
                <h2>{i18n.t('common:chat_start_first_conversation')}</h2>
                <p>{i18n.t('common:chat_description')}</p>
              </IonText>
            </div>
          ),
        }}
      >
        <div
          ref={chipRef}
          className="chat-chip-container-wrapper chat-chip-top"
          style={{ zIndex: hasNewMessage ? 1000 : -1 }}
        >
          <NewMessageChip
            hidden={!hasNewMessage}
            onClick={() => onNewMessageClick()}
          />
        </div>
        <div className="chat-thread-list" style={{ top: -chipHeight }}>
          {conversations.map((conversation) => (
            <ConversationPreview
              key={conversation.id}
              conversation={conversation}
            />
          ))}
        </div>
        <InfiniteScroll
          className="content-center"
          onInfinite={() => getMoreForInfiniteScroll(prevCursor)}
          disabled={!prevCursor || waitingOnFirstLoad}
          threshold={0.25}
          loader={<IonSpinner className="ion-align-self-center" />}
        />
      </LoadingRetry>
    </IonContent>
  );
};

export { ConversationList };
